import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, TrendingDown, Activity } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function StockDetailPage() {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const [stock, setStock] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStockDetails();
  }, [symbol]);

  const fetchStockDetails = async () => {
    try {
      setLoading(true);
      
      // Fetch current stock data
      const recResponse = await fetch(`${API_URL}/api/recommendations/latest?limit=100`);
      const recData = await recResponse.json();
      
      if (recData.success) {
        const stockData = recData.data.find(s => s.symbol === symbol);
        setStock(stockData);
      }
      
      // Fetch historical data
      const histResponse = await fetch(`${API_URL}/api/recommendations/history?symbol=${symbol}&days=7`);
      const histData = await histResponse.json();
      
      if (histData.success) {
        setHistory(histData.data);
      }
    } catch (err) {
      console.error('Error fetching stock details:', err);
      setError('Failed to load stock details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading stock details...</p>
        </div>
      </div>
    );
  }

  if (error || !stock) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="text-center">
          <p className="text-destructive mb-4">{error || 'Stock not found'}</p>
          <button onClick={() => navigate('/')} className="btn-primary">
            Back to Recommendations
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <header className="bg-background border-b border-border px-4 py-4">
        <div className="max-w-7xl mx-auto">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-3"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to recommendations
          </button>
          
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold font-display">{stock.symbol}</h1>
              <p className="text-sm text-muted-foreground mt-1">{stock.name}</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-green-500" data-testid="stock-score">
                {stock.score.toFixed(0)}
              </div>
              <div className="text-xs text-muted-foreground">Score</div>
            </div>
          </div>
        </div>
      </header>

      <div className="px-4 py-6 max-w-7xl mx-auto space-y-6">
        {/* Key Metrics */}
        <div className="grid grid-cols-2 gap-3">
          <div className="stats-card">
            <div className="text-sm text-muted-foreground mb-1">Buy Rating</div>
            <div className="text-2xl font-bold" data-testid="buy-rating">{stock.buy_rating}</div>
            <div className="text-xs text-muted-foreground mt-1">Analyst consensus</div>
          </div>
          
          <div className="stats-card">
            <div className="text-sm text-muted-foreground mb-1">Potential Growth</div>
            <div className="text-2xl font-bold text-green-500" data-testid="growth-potential">
              {stock.potential_growth_pct}%
            </div>
            <div className="text-xs text-muted-foreground mt-1">Expected upside</div>
          </div>
          
          {stock.price && (
            <div className="stats-card">
              <div className="text-sm text-muted-foreground mb-1">Current Price</div>
              <div className="text-2xl font-bold" data-testid="current-price">
                ${stock.price.toFixed(2)}
              </div>
              {stock.change_pct !== undefined && (
                <div className={`text-xs mt-1 ${stock.change_pct >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {stock.change_pct >= 0 ? '▲' : '▼'} {Math.abs(stock.change_pct).toFixed(2)}%
                </div>
              )}
            </div>
          )}
          
          {stock.volume && (
            <div className="stats-card">
              <div className="text-sm text-muted-foreground mb-1">Volume</div>
              <div className="text-2xl font-bold" data-testid="volume">
                {(stock.volume / 1000000).toFixed(1)}M
              </div>
              {stock.is_most_active && (
                <div className="text-xs text-blue-500 mt-1">Most active</div>
              )}
            </div>
          )}
        </div>

        {/* Score Breakdown */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Score Calculation</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Buy Rating</span>
              <span className="text-lg font-semibold">{stock.buy_rating}</span>
            </div>
            <div className="flex items-center justify-center text-muted-foreground">×</div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Potential Growth %</span>
              <span className="text-lg font-semibold text-green-500">{stock.potential_growth_pct}%</span>
            </div>
            <div className="border-t border-border pt-3 flex items-center justify-between">
              <span className="text-sm font-semibold">Final Score</span>
              <span className="text-2xl font-bold text-green-500">{stock.score.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* 7-Day Trend Chart */}
        {history.length > 0 && (
          <div className="card">
            <h2 className="text-lg font-semibold mb-4">7-Day Score Trend</h2>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={history}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis 
                  dataKey="as_of" 
                  stroke="#888"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(date) => format(new Date(date), 'MM/dd')}
                />
                <YAxis stroke="#888" tick={{ fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1a1a1a', 
                    border: '1px solid #333',
                    borderRadius: '8px'
                  }}
                  labelFormatter={(date) => format(new Date(date), 'MMM dd, yyyy')}
                />
                <Line 
                  type="monotone" 
                  dataKey="score" 
                  stroke="#10b981" 
                  strokeWidth={2}
                  dot={{ fill: '#10b981', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Additional Info */}
        {stock.sector && (
          <div className="card">
            <h2 className="text-lg font-semibold mb-3">Additional Information</h2>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Sector</span>
                <span className="text-sm font-medium">{stock.sector}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Last Updated</span>
                <span className="text-sm font-medium">{stock.last_updated}</span>
              </div>
              {stock.is_most_active && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Status</span>
                  <span className="badge badge-blue">Most Active</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default StockDetailPage;
